import { useCallback, useRef, useState, useEffect } from 'react';
import { GameEngine, type GameState as BackendGameState, PieceType } from '@game/engine';

/**
 * Hook for managing game state using backend GameEngine
 * Frontend only handles state updates and UI callbacks
 */
export function useGameState() {
  const engineRef = useRef<GameEngine>(new GameEngine());
  const [gameState, setGameState] = useState<BackendGameState>(engineRef.current.getState());
  const lastTimeRef = useRef<number>(Date.now());

  // Helper to update React state from engine
  const updateState = useCallback(() => {
    setGameState(engineRef.current.getState());
  }, []);

  // Game loop - runs tick and updates state every frame
  useEffect(() => {
    let animationId: number;

    const gameLoop = () => {
      const now = Date.now();
      const deltaTime = (now - lastTimeRef.current) / 1000; // Convert to seconds
      lastTimeRef.current = now;

      engineRef.current.tick(deltaTime);
      updateState();

      animationId = requestAnimationFrame(gameLoop);
    };

    animationId = requestAnimationFrame(gameLoop);

    return () => cancelAnimationFrame(animationId);
  }, [updateState]);

  const placePiece = useCallback((cursorX: number, cursorY: number): boolean => {
    const success = engineRef.current.placePiece(cursorX, cursorY);
    if (success) {
      updateState();
    }
    return success;
  }, [updateState]);

  const removePiece = useCallback((pieceId: string): void => {
    engineRef.current.removePiece(pieceId);
    updateState();
  }, [updateState]);

  const selectPieceType = useCallback((type: PieceType | null): void => {
    engineRef.current.selectPieceType(type);
    updateState();
  }, [updateState]);

  const rotatePiece = useCallback((clockwise: boolean): void => {
    engineRef.current.rotatePiece(clockwise);
    updateState();
  }, [updateState]);

  const mirrorPiece = useCallback((): void => {
    engineRef.current.mirrorPiece();
    updateState();
  }, [updateState]);

  const clearBoard = useCallback((): void => {
    engineRef.current.clearBoard();
    updateState();
  }, [updateState]);

  const expandMapSize = useCallback((dimension: 'width' | 'height'): boolean => {
    const success = engineRef.current.expandGrid(dimension);
    if (success) {
      updateState();
    }
    return success;
  }, [updateState]);

  const shrinkMapSize = useCallback((dimension: 'width' | 'height'): boolean => {
    const success = engineRef.current.shrinkGrid(dimension);
    if (success) {
      updateState();
    }
    return success;
  }, [updateState]);

  const loadTiling = useCallback((tilingStr: string): boolean => {
    const success = engineRef.current.loadTiling(tilingStr as any);
    if (success) {
      updateState();
    }
    return success;
  }, [updateState]);

  const exportTiling = useCallback((): string => {
    return engineRef.current.exportTiling();
  }, []);

  const prestige = useCallback((): boolean => {
    const success = engineRef.current.prestige();
    if (success) {
      updateState();
    }
    return success;
  }, [updateState]);

  const upgradePiece = useCallback((pieceId: string): boolean => {
    const success = engineRef.current.upgradePiece(pieceId);
    if (success) {
      updateState();
    }
    return success;
  }, [updateState]);

  return {
    ...gameState,
    placePiece,
    removePiece,
    selectPieceType,
    rotatePiece,
    mirrorPiece,
    clearBoard,
    expandMapSize,
    shrinkMapSize,
    loadTiling,
    exportTiling,
    prestige,
    upgradePiece,
    engine: engineRef.current,
  };
}
